import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import ReactTable from 'react-table';
import './MaterialTable.css';
import {
  Col,
  FormControl,
  FormGroup,
} from 'react-bootstrap';
import Filters from '../../components/Filters/Filters';
import PaginationTable from '../../components/Pagination/PaginationTable';
import getSortHeaderProps from '../../utils/get-sorted-headers';
import { withTranslation } from 'react-i18next';
import { toFixed } from '../../utils/numbers';
import {
  InventoryPlace,
  MaterialType,
} from '../../constants/MaterialModels';

const WEIGHED_COLUMNS = [
  {
    headerText: 'Bins',
    accessor: d => d,
    width: 130,
    id: 'bins',
  },
  {
    headerText: 'Balance',
    accessor: d => d,
    id: 'currentBalance',
  },
  {
    headerText: 'Used Today',
    accessor: d => d,
    id: 'usageToday',
    Cell: row => `${toFixed(row.value.usageToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Shipped Today',
    accessor: d => d,
    id: 'shipmentToday',
    Cell: row => `${toFixed(row.value.shipmentToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Inventory',
    accessor: d => d,
    id: 'sumBalanceForLocation',
    width: 80,
    Cell: row => row.value.sumBalanceForLocation ? InventoryPlace.Location : InventoryPlace.Plant,
  },
];

const METERED_COLUMNS = [
  {
    headerText: 'Pump',
    accessor: d => d,
    id: 'pumpId',
    maxWidth: 120,
  },
  {
    headerText: 'Balance',
    accessor: d => d,
    id: 'currentBalance',
  },
  {
    headerText: 'Used Today',
    accessor: d => d,
    id: 'usageToday',
    Cell: row => `${toFixed(row.value.usageToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Shipped Today',
    accessor: d => d,
    id: 'shipmentToday',
    Cell: row => `${toFixed(row.value.shipmentToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Inventory',
    accessor: d => d,
    id: 'sumBalanceForLocation',
    width: 80,
    Cell: row => row.value.sumBalanceForLocation ? InventoryPlace.Location : InventoryPlace.Plant,
  },
];

const OTHER_COLUMNS = [
  {
    headerText: 'Balance',
    accessor: d => d,
    id: 'currentBalance',
  },
  {
    headerText: 'Used Today',
    accessor: d => d,
    id: 'usageToday',
    Cell: row => `${toFixed(row.value.usageToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Shipped Today',
    accessor: d => d,
    id: 'shipmentToday',
    Cell: row => `${toFixed(row.value.shipmentToday, 2)} ${row.value.itemInventoryUnitSymbol}`,
  },
  {
    headerText: 'Inventory',
    accessor: d => d,
    id: 'sumBalanceForLocation',
    width: 80,
    Cell: row => row.value.sumBalanceForLocation ? InventoryPlace.Location : InventoryPlace.Plant,
  },
];

const COLUMN_MAP = {
  weighed: WEIGHED_COLUMNS,
  metered: METERED_COLUMNS,
  other: OTHER_COLUMNS,
  unassigned: [],
};

class MaterialTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sorted: [],
      filter: '',
    };
  }

  getDevicesName(deviceId) {
    const { devicesStore } = this.props;
    const device = devicesStore.getDeviceById(deviceId);
    return device && device.description ? device.description : '-';
  }

  getCell(c) {
    const { materialStore } = this.props;

    if (c.id === 'bins') {
      return row => {
        if (row.original.bins?.length) {
          let bins = '';
          let activeBins = row.original.scaleId ? row.original.bins : row.original.bins.filter(v => v.isActive);

          activeBins.forEach((v, i) => {
            bins += `${this.getDevicesName(v.binId)}`;
            if (v.linkedBinId) {
              bins += ` | ${this.getDevicesName(v.linkedBinId)}`;
            }
            if (activeBins.length - 1 !== i) {
              bins += ' | ';
            }
          });
          return bins;
        }
        return '';
      };
    } else if (c.id === 'pumpId') {
      return row => this.getDevicesName(row.original.pumpId);
    } else if (c.id === 'currentBalance') {
      return row => `${materialStore.getDevicesBalance(row.original)} ${row.original.itemInventoryUnitSymbol}`;
    } else {
      return c.Cell;
    }
  }

  getColumns = () => {
    const { materialStore, t } = this.props;
    const { sorted } = this.state;

    return [
      {
        headerText: t('Material'),
        accessor: d => d,
        Cell: d => (<div>{materialStore.getMaterialName(d.original)}</div>),
        id: 'code',
        className: 'sticky',
        headerClassName: 'sticky',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Code'),
        accessor: d => d,
        Cell: d => (<div>{d.original.code}</div>),
        id: 'code',
        className: 'sticky',
        headerClassName: 'sticky',
        ...getSortHeaderProps(sorted),
      },
      ...COLUMN_MAP[this.props.type].map(c => ({
        ...c,
        Cell: this.getCell(c),
        headerText: t(c.headerText),
        ...getSortHeaderProps(sorted),
      })),
      {
        headerText: '',
        width: 130,
        id: 'actions',
        accessor: d => d,
        Cell: d => (<div className="text-right">{this.renderActionCell(d)}</div>),
        sortable: false,
      },
    ];
  };

  renderActionCell = row => {
    const { t } = this.props;
    const { assignment } = row.value;
    const actions = [];

    if (assignment === MaterialType.Weighed || assignment === MaterialType.Metered) {
      actions.push(
        <a
          className="btn btn-sm btn-default btn-action material-action"
          key={`edit-${row.value.itemId}`}
          title={t('Edit')}
          onClick={() => this.props.onEditMaterial(row.value)}
        >
          <i className="fa fa-fw fa-pencil" />
        </a>
      );
    }

    if (assignment !== MaterialType.None) {
      actions.push(
        <a
          className="btn btn-sm btn-default btn-action material-action"
          key={`ship-${row.value.itemId}`}
          title={t('Ship In')}
          onClick={(e) => {
            e.stopPropagation();
            this.setState({
              selected: row.index,
            });
            this.props.onShipInMaterial(row.value.inventoryItemId);
          }
          }
        >
          <i className="fa fa-fw fa-truck" />
        </a>,
        <a
          className="btn btn-sm btn-default btn-action material-action"
          key={`adjust-${row.value.itemId}`}
          title={t('Adjust')}
          onClick={(e) => {
            e.stopPropagation();
            this.setState({
              selected: row.index,
            });
            this.props.onAdjustMaterial(row.value.itemId);
          }}
        >
          <i className="fa fa-fw fa-sliders" />
        </a>,
      );
    }
    if (row.value.inventoryItemId) {
      actions.push(
        <a
          className="btn btn-sm btn-default btn-action material-action"
          key={`history-${row.value.itemId}`}
          title={t('History')}
          onClick={(e) => {
            e.stopPropagation();
            this.setState({
              selected: row.index,
            });
            if (this.props.onHistoryMaterial) {
              this.props.onHistoryMaterial(row.value.inventoryItemId);
            }
          }}
        >
          <i className="fa fa-fw fa-history" />
        </a>
      );
    }

    return <div>{actions}</div>;
  };

  handleChange = (e) => {
    const filter = e.target.value;
    this.setState({ filter });
  };

  render() {
    const { items, t } = this.props;
    const { filter } = this.state;

    return (
      <div>
        <Col sm={4} className={'no-padding'}>
          <FormGroup>
            <FormControl
              type="text"
              value={filter}
              placeholder={t('Search')}
              onChange={this.handleChange}
            />
          </FormGroup>
        </Col>
        <Filters filter={filter} data={items} renderData={(items) => (
          <ReactTable
            className="-highlight material-table"
            noDataText={' '}
            data={items}
            columns={this.getColumns()}
            sorted={this.state.sorted}
            onSortedChange={sorted => this.setState({ sorted })}
            showPaginationTop={false}
            showPaginationBottom={true}
            showPageSizeOptions={true}
            PaginationComponent={PaginationTable}
            resizable={false}
            minRows={1}
            defaultPageSize={100}
            getTrProps={(state, rowInfo) => {
              if (rowInfo && rowInfo.row) {
                return {
                  onClick: (e) => {
                    e.stopPropagation();
                    if (rowInfo.original.assignment !== MaterialType.Other) {
                      this.props.onEditMaterial(rowInfo.original);
                      this.setState({
                        selected: rowInfo.index,
                      });
                    } else {
                      this.setState({
                        selected: null,
                      });
                    }
                  },
                  className: rowInfo.index === this.state.selected ? 'row-selected': '',
                };
              }
              return {};
            }}
          />
        )} />
      </div>
    );
  }
}

export default withTranslation()(inject('materialStore', 'shipHistoryStore', 'devicesStore')(observer(MaterialTable)));
